/**
 * @fileoverview The mock of Update Role Permissions (PATCH /authorization/role-permissions).
 * @description Validates a bulk grant/revoke request against the mock store with the rules the real
 * backend enforces — 1-10 distinct roles, each with 1-50 distinct permissions (ids compared
 * case-insensitively, as SQL Server does, so one id in two letter cases is a repeat) and the
 * `ifMatch` (its row version as a strong ETag) it was loaded at — and, only when every role and permission
 * is valid, applies it all, so a refusal saves nothing. Super Admin always keeps every permission
 * (409/2019), a stale `ifMatch` is 412/2014, an unknown role 404/2002, and an unknown permission,
 * or an inactive one being granted, 422/2006. A role whose grants really changed advances its row
 * version once. Answers in the real endpoint's shape. Never imported outside this feature's
 * mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  bumpRoleVersion,
  findPermission,
  findSalesRole,
  isHeld,
  roleRowVersion,
  rolePermissionVersion,
  setGrant,
} from './capabilities-mock-store'

const SUPER_ADMIN_CODE = 'SUPER_ADMIN'
const MAX_ROLES = 10
const MAX_PERMISSIONS = 50

interface Problem {
  ok: false
  status: number
  appCode: string
  message: string
}

interface PlannedChange {
  permissionId: string
  permissionCode: string
  granted: boolean
}

interface PlannedRole {
  roleId: string
  roleCode: string
  changes: PlannedChange[]
}

export type UpdateRolePermissionsResult = { ok: true; body: object } | Problem

/**
 * @description Builds a refusal.
 * @param {number} status - The HTTP status.
 * @param {string} appCode - The application code.
 * @param {string} message - The message.
 * @returns {Problem} The refusal.
 */
function problem(status: number, appCode: string, message: string): Problem {
  return { ok: false, status, appCode, message }
}

const INVALID_BODY = problem(400, '0001', 'The request body is not valid.')

/**
 * @description Reads and checks one permission change of a role.
 * @param {Partial<{ permissionId: unknown; granted: unknown }>} entry - The untrusted change.
 * @returns {PlannedChange | Problem} The change, or the first problem with it.
 */
function planChange(entry: Partial<{ permissionId: unknown; granted: unknown }>) {
  if (typeof entry.permissionId !== 'string' || typeof entry.granted !== 'boolean') {
    return INVALID_BODY
  }

  const permission = findPermission(entry.permissionId)
  if (!permission || (entry.granted && permission.status !== 'ACTIVE')) {
    return problem(422, '2006', 'That permission does not exist or is inactive.')
  }

  return {
    permissionId: entry.permissionId,
    permissionCode: permission.permissionCode,
    granted: entry.granted,
  }
}

/**
 * @description Whether a list of ids names one id twice. Ids are compared case-insensitively, as
 * the backend does; an entry that isn't a string is left for the shape checks to refuse.
 * @param {unknown[]} ids - The ids as sent, untrusted.
 * @returns {boolean} True when an id repeats.
 */
function repeatsAnId(ids: unknown[]): boolean {
  const seen = new Set<string>()
  for (const id of ids) {
    if (typeof id !== 'string') {
      continue
    }

    const key = id.toLowerCase()
    if (seen.has(key)) {
      return true
    }
    seen.add(key)
  }

  return false
}

/**
 * @description Checks the shape of one role's entry.
 * @param {unknown} raw - The untrusted entry.
 * @returns {{ roleId: string; ifMatch: string; changes: unknown[] } | undefined} The parts, or
 * undefined when the entry is malformed.
 */
function readRoleEntry(raw: unknown) {
  const entry = (raw ?? {}) as Partial<{ roleId: unknown; ifMatch: unknown; permissions: unknown }>
  const changes = entry.permissions
  if (
    typeof entry.roleId !== 'string' ||
    typeof entry.ifMatch !== 'string' ||
    !Array.isArray(changes) ||
    changes.length === 0 ||
    changes.length > MAX_PERMISSIONS
  ) {
    return undefined
  }

  return { roleId: entry.roleId, ifMatch: entry.ifMatch, changes: changes as unknown[] }
}

/**
 * @description Reads and checks one role's entry: its shape, the role, that it is not Super Admin,
 * each of its permission changes and, last, its `ifMatch`. The backend's contract doesn't state the
 * order its checks run in; this one answers the permanent refusals before the retryable 412.
 * @param {unknown} raw - The untrusted entry.
 * @returns {PlannedRole | Problem} The planned role, or the first problem with it.
 */
function planRole(raw: unknown): PlannedRole | Problem {
  const entry = readRoleEntry(raw)
  if (!entry) {
    return INVALID_BODY
  }

  const sentIds = entry.changes.map(
    (item) => (item as { permissionId?: unknown } | null)?.permissionId,
  )
  if (repeatsAnId(sentIds)) {
    return INVALID_BODY
  }

  const role = findSalesRole(entry.roleId)
  if (!role) {
    return problem(404, '2002', 'That role does not exist.')
  }
  if (role.roleCode === SUPER_ADMIN_CODE) {
    return problem(409, '2019', 'Super Admin always keeps every permission.')
  }

  const planned: PlannedChange[] = []
  for (const change of entry.changes as Partial<{ permissionId: unknown; granted: unknown }>[]) {
    const next = planChange(change)
    if ('ok' in next) {
      return next
    }
    planned.push(next)
  }

  // The version check comes last: a request that can never succeed (a protected role, an unknown
  // or inactive permission) is refused for that reason whatever version it names, and 412 only
  // tells the caller "reload and try again" about a request that was otherwise acceptable.
  if (entry.ifMatch !== `"${roleRowVersion(role.roleCode)}"`) {
    return problem(412, '2014', 'The role changed since it was loaded.')
  }

  return { roleId: entry.roleId, roleCode: role.roleCode, changes: planned }
}

/**
 * @description Applies one planned role and describes it as the endpoint's answer does.
 * @param {PlannedRole} role - The checked role.
 * @returns {object} The role's part of the answer.
 */
function applyRole(role: PlannedRole) {
  const changed = role.changes.map((change) =>
    setGrant(role.roleCode, change.permissionCode, change.granted),
  )
  const anyChanged = changed.some(Boolean)
  if (anyChanged) {
    bumpRoleVersion(role.roleCode)
  }

  return {
    roleId: role.roleId,
    permissionVersion: rolePermissionVersion(role.roleCode),
    roleRowVersion: roleRowVersion(role.roleCode),
    etag: `"${roleRowVersion(role.roleCode)}"`,
    changed: anyChanged,
    permissions: role.changes.map((change, index) => ({
      permissionId: change.permissionId,
      rolePermissionId: `mock-mapping-${role.roleCode}-${change.permissionCode}`,
      status: isHeld(role.roleCode, change.permissionCode) ? 'ACTIVE' : 'REVOKED',
      changed: changed[index] ?? false,
    })),
  }
}

/**
 * @description Validates a bulk update and, if every role and permission is valid, applies it all.
 * @param {unknown} body - The request body, untrusted.
 * @returns {UpdateRolePermissionsResult} The answer, or the first problem found (nothing is saved
 * when there is one).
 */
export function applyRolePermissionUpdate(body: unknown): UpdateRolePermissionsResult {
  const roles = (body as { roles?: unknown } | null)?.roles
  if (!Array.isArray(roles) || roles.length === 0 || roles.length > MAX_ROLES) {
    return INVALID_BODY
  }

  if (repeatsAnId(roles.map((raw) => (raw as { roleId?: unknown } | null)?.roleId))) {
    return INVALID_BODY
  }

  const planned: PlannedRole[] = []
  for (const raw of roles) {
    const next = planRole(raw)
    if ('ok' in next) {
      return next
    }
    planned.push(next)
  }

  const applied = planned.map(applyRole)
  const changedCount = applied
    .flatMap((role) => role.permissions)
    .filter((mapping) => mapping.changed).length

  return { ok: true, body: { changedCount, roles: applied } }
}
